import { Button, Card, Typography } from '@mui/material'
import { Box } from '@mui/system'
import HorizontalItem from 'components/container/HorizontalItem'
import InfoTip from 'components/InfoTip'
import SupplyModal from 'components/Modal/SupplyModal'
import Typoleton from 'components/container/Typoleton'
import VerticalItem from 'components/container/VerticalItem'
import { AssetData } from 'constants/assets'
import CoreSwitch from 'core/CoreSwitch'
import CoreTable from 'core/CoreTable'
import CoreToken from 'core/CoreToken'
import usePoolTx from 'hooks/usePoolTx'
import { useState } from 'react'
import { useModal } from 'react-modal-hook'
import { assetService } from 'services/assetService'
import WithdrawModal from 'components/Modal/WithdrawModal'
import BoostBorrowingPowerModal from 'components/Modal/BoostBorrowingPowerModal'
import { PERCENT, ZERO } from 'constants/number'
import { MarketResponse } from 'interfaces/contracts/edgePool'
import BigNumber from 'bignumber.js'
import { toA } from 'utils/display'
import { poolService } from 'services/poolService'
import { oracleStore, poolStore } from 'stores'
import { observer } from 'mobx-react-lite'
import TableRowMobile from 'components/container/TableRowMobile'

const SupplyMarketTable = observer(() => {
  const poolAddr = poolService.poolAddress
  const { marketInfo, supplyMarketList } = poolStore.getPool(poolAddr)
  const prices = oracleStore.getPrices(poolAddr)
  const { assets } = assetService
  const { sendSupply, sendWithdraw, sendLock, sendUnlock } = usePoolTx()

  const [selectedAsset, setSelectedAsset] = useState<
    AssetData & { etoken?: string }
  >()
  const [showSupplyModal, hideSupplyModal] = useModal(() => {
    if (!selectedAsset) return <></>
    return (
      <SupplyModal
        assetData={selectedAsset}
        onClose={hideSupplyModal}
        sendSupply={sendSupply}
      />
    )
  }, [selectedAsset, sendSupply])
  const [showWithdrawModal, hideWithdrawModal] = useModal(() => {
    if (!selectedAsset) return <></>

    return (
      <WithdrawModal
        onClose={hideWithdrawModal}
        sendWithdraw={sendWithdraw}
        assetData={selectedAsset}
      />
    )
  }, [selectedAsset, sendWithdraw])
  const [showBoostingPowerModal, hideBoostingPowerModal] = useModal(() => {
    if (!selectedAsset) return <></>
    const { locked, walletSupplyBalance, config } =
      marketInfo[selectedAsset.terraAddress]

    const change = walletSupplyBalance
      .times(prices[selectedAsset.terraAddress])
      .times(config.collateral_factor)
      .times(locked ? -1 : 1)
    const sendTx = locked ? sendUnlock : sendLock
    return (
      <BoostBorrowingPowerModal
        onClose={hideBoostingPowerModal}
        change={change}
        sendTx={sendTx}
        assetData={selectedAsset}
      />
    )
  }, [marketInfo, selectedAsset, sendUnlock, sendLock])

  const handleOnSwitch = (assetData: AssetData) => {
    setSelectedAsset(assetData)
    showBoostingPowerModal()
  }
  const handleOnSupply = (assetData: AssetData) => {
    setSelectedAsset(assetData)
    showSupplyModal()
  }
  const handleOnWithdraw = (assetData: AssetData, etoken: string) => {
    setSelectedAsset({ ...assetData, etoken })
    showWithdrawModal()
  }

  const getRow = (_market: MarketResponse, responsive: boolean = false) => {
    const { underlying } = _market
    const {
      walletSupplyBalance: mySupplyBal,
      supplyAPY,
      locked,
      config,
      totalCredit,
      totalLoan,
    } = marketInfo[underlying]

    const assetData = assets[underlying]
    const price = prices?.[underlying]
    const collateralFactor = new BigNumber(config.collateral_factor).times(
      PERCENT,
    )
    const disableCollateral = collateralFactor.eq(ZERO)
    const totalSupplyUSD = totalCredit.plus(totalLoan).times(price)

    if (responsive) {
      const isLasted =
        supplyMarketList &&
        supplyMarketList.indexOf(_market) === supplyMarketList.length - 1
      return (
        <TableRowMobile key={underlying} border={!isLasted}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <CoreToken assetData={assetData} size={32} withName fontBold />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Button
                variant="contained"
                onClick={() => handleOnSupply(assetData)}
                sx={{ height: '40px', width: '100px' }}
              >
                Supply
              </Button>
              <Button
                variant="outlined"
                onClick={() => handleOnWithdraw(assetData, _market.etoken_addr)}
                sx={{ height: '40px', width: '100px' }}
              >
                Withdraw
              </Button>
            </Box>
          </Box>
          <HorizontalItem>
            <Typography variant="body1"> Supply APY </Typography>
            <Typography variant="body1">{toA(supplyAPY)}%</Typography>
          </HorizontalItem>
          <HorizontalItem>
            <Typography variant="body1"> My supply bal. </Typography>
            <Typography variant="body1">
              {`${toA(mySupplyBal)} ${assetData.symbol}`}
            </Typography>
          </HorizontalItem>
          <HorizontalItem>
            <Typography variant="body1"> Boost borrowing power </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <CoreSwitch
                checked={locked}
                onChange={() => handleOnSwitch(assetData)}
                sx={{ marginRight: '4px' }}
                disabled={disableCollateral}
              />
              <InfoTip
                kind="Collateral factor"
                text={`${toA(collateralFactor)}%`}
              />
            </Box>
          </HorizontalItem>
        </TableRowMobile>
      )
    }
    return [
      <Box display="flex" gap="12px">
        <CoreToken assetData={assetData} size={32} />
        <VerticalItem sx={{ alignItems: 'start' }}>
          <Typography variant="body1" fontWeight={600}>
            {assetData.symbol}
          </Typography>
          <Typography variant="caption">
            Total supply ${toA(totalSupplyUSD)}
          </Typography>
        </VerticalItem>
      </Box>,
      <Typography variant="body1">{toA(supplyAPY)}%</Typography>,
      <VerticalItem>
        <Typography variant="body1">
          {`${toA(mySupplyBal)} ${assetData.symbol}`}
        </Typography>
        <Typography variant="caption">
          ${price && toA(price.times(mySupplyBal))}
        </Typography>
      </VerticalItem>,
      <VerticalItem>
        <CoreSwitch
          checked={locked}
          onChange={() => handleOnSwitch(assetData)}
          disabled={collateralFactor.eq(ZERO)}
        />
        {disableCollateral ? (
          <Typography variant="caption">cannot be collateral</Typography>
        ) : (
          <Typography variant="caption" sx={{ paddingX: '4px' }}>
            {`by ${toA(collateralFactor)}% of supply bal.`}
          </Typography>
        )}
      </VerticalItem>,
      <Box display="flex" gap="16px" justifyContent="center">
        <Button
          variant="contained"
          onClick={() => handleOnSupply(assetData)}
          sx={{ height: '40px', width: '100px' }}
        >
          Supply
        </Button>
        <Button
          variant="outlined"
          onClick={() => handleOnWithdraw(assetData, _market.etoken_addr)}
          sx={{ height: '40px', width: '100px' }}
        >
          Withdraw
        </Button>
      </Box>,
    ]
  }

  return (
    <Card sx={{ border: 'none' }}>
      <Typography variant="h6" fontSize={20}>
        Supply market
      </Typography>
      <br />
      <CoreTable
        headers={[
          <Typography variant="caption">Asset</Typography>,
          <Typography variant="caption">Supply APY</Typography>,
          <Typography variant="caption">My supply bal.</Typography>,
          <InfoTip
            kind="Boost borrowing power with collateral"
            text="Boost borrowing power"
            variant="caption"
          />,
          <></>,
        ]}
        rows={
          supplyMarketList?.map((market) => {
            return getRow(market)
          }) || Array(3).fill(LoadingTable)
        }
        responsive={supplyMarketList?.map((market) => {
          return getRow(market, true)
        })}
      />
    </Card>
  )
})

export default SupplyMarketTable
// TODO: loading table to real size
const LoadingTable = [
  <CoreToken size={32} />,
  // <VerticalItem>
  //   <Typoleton variant="body1" />
  //   <Typoleton variant="caption" />
  // </VerticalItem>,
  <Typoleton variant="body1" />,
  <VerticalItem>
    <Typoleton variant="body1" />
    <Typoleton variant="caption" />
  </VerticalItem>,
  <CoreSwitch />,
  <Box display="flex" gap="16px" justifyContent="center">
    <Button
      variant="contained"
      disabled
      sx={{ height: '40px', width: '100px' }}
    >
      Supply
    </Button>
    <Button variant="outlined" disabled sx={{ height: '40px', width: '100px' }}>
      Withdraw
    </Button>
  </Box>,
]
